import React from "react";
import { Link } from "react-router-dom";
import logo from "./assets/logo.png";

const GoogleAPIDisclosure = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-slate-900 px-6 py-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <Link to="/" className="text-orange-500 text-xl font-bold">
            <img src={logo} alt="Logo" className="h-12 w-auto" />
          </Link>
          <Link
            to="/"
            className="text-white hover:text-orange-500 transition-colors"
          >
            Back to Home
          </Link>
        </div>
      </nav>

      {/* Google API Disclosure Content */}
      <div className="max-w-4xl mx-auto px-6 py-16">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">
          Google API Services Disclosure
        </h1>
        <p className="text-sm text-gray-600 mb-8">
          Last updated: May 20, 2026
        </p>

        <div className="prose prose-lg text-gray-600 space-y-6">
          <p>
            At Jindo Technologies, we are committed to protecting user privacy and
            handling Google user data responsibly and securely.
          </p>

          <p className="font-semibold text-gray-900">
            Jindo Technologies' use and transfer of information received from Google
            APIs adheres to the{" "}
            <a
              href="https://developers.google.com/terms/api-services-user-data-policy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-orange-500 hover:text-orange-600 underline"
            >
              Google API Services User Data Policy
            </a>
            , including the Limited Use requirements.
          </p>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Google Services We Integrate With
            </h2>
            <p className="mb-4">
              Jindo integrates with the following Google services based on features enabled by the user:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Gmail</li>
              <li>Google Calendar</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Why We Request Access
            </h2>
            <p className="mb-4">
              Jindo accesses Google Workspace data only to provide user-requested meeting and productivity features.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-2 mt-4">
              Gmail
            </h3>
            <p className="mb-4">
              Jindo uses Gmail access solely to allow users to send follow-up emails directly from the application after meetings.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-2 mt-4">
              Google Calendar
            </h3>
            <p className="mb-2">
              Jindo uses Google Calendar access to:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>display upcoming meetings</li>
              <li>access meeting links for joining meetings</li>
              <li>retrieve meeting attendee information</li>
              <li>generate meeting summaries with participant context</li>
              <li>populate recipient information in follow-up emails</li>
            </ul>

            <p className="mt-4">
              Access is limited to the minimum data required to provide these features.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              How We Use Google User Data
            </h2>
            <p className="mb-4">
              Google user data is used only to provide functionality explicitly requested by the user within the Jindo platform.
            </p>
            <p className="mb-2 font-semibold text-gray-900">
              We do not use Google Workspace data for:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>advertising purposes</li>
              <li>selling data to third parties</li>
              <li>unrelated analytics or profiling</li>
            </ul>
            <p className="mb-2">
              Google user data is processed only for features such as:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>displaying scheduled meetings</li>
              <li>identifying meeting participants</li>
              <li>generating summaries and action items</li>
              <li>preparing and sending follow-up emails from the application</li>
            </ul>
            <p className="mb-2 font-semibold text-gray-900">
              We do not transfer Google user data to third parties except:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>as necessary to provide the requested application functionality</li>
              <li>to comply with applicable laws</li>
              <li>with explicit user consent</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Limited Use Compliance
            </h2>
            <p className="mb-4">
              Jindo Technologies' use and transfer of information received from Google APIs adheres to the Google API Services User Data Policy, including the Limited Use requirements.
            </p>
            <p className="mb-2 font-semibold text-gray-900">
              Jindo does not:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>sell Google user data</li>
              <li>use Google user data for advertising</li>
              <li>allow humans to read user data except when required for security, support, or legal compliance</li>
              <li>use Google Workspace data to train generalized AI or machine learning models</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              AI and Machine Learning Usage
            </h2>
            <p className="mb-4">
              Google Workspace data accessed by Jindo is used only to provide user-requested meeting productivity features such as summaries, action items, and follow-up email generation.
            </p>
            <p>
              Google Workspace data is not used to train generalized artificial intelligence or machine learning models.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Data Security
            </h2>
            <p className="mb-4">
              Jindo Labs implements industry-standard security measures to protect user data, including:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>encrypted transmission using HTTPS/TLS</li>
              <li>secure authentication and access controls</li>
              <li>restricted internal access to authorized personnel only</li>
              <li>secure cloud infrastructure and monitoring</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Human Access to Data
            </h2>
            <p className="mb-4">
              Jindo Labs personnel do not access Google user data unless:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>required for debugging or support,</li>
              <li>necessary for security purposes,</li>
              <li>or explicitly authorized by the user.</li>
            </ul>
            <p className="mt-4">
              Any such access is limited and protected by strict confidentiality obligations.
            </p>
          </section>

          {/* <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              User Control and Revocation
            </h2>
            <p className="mb-4">
              Users may revoke Jindo's access to their Google account at any
              time through their Google Account permissions settings:
            </p>
            <p>
              <a
                href="https://myaccount.google.com/permissions"
                target="_blank"
                rel="noopener noreferrer"
                className="text-orange-500 hover:text-orange-600 underline font-medium"
              >
                Google Account Permissions
              </a>
            </p>
          </section> */}

          <section className="mt-12 pt-8 border-t border-gray-300">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Contact</h2>
            <p className="mb-2">
              If you have questions regarding this disclosure or our handling of
              Google user data, please contact:
            </p>
            <p className="font-semibold">Jindo Technologies, LLC</p>
            <p>
              <a
                href="mailto:support@jindolabs.com"
                className="text-orange-500 hover:text-orange-600 underline"
              >
                support@jindolabs.com
              </a>
            </p>
          </section>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8 px-6 mt-16">
        <div className="max-w-6xl mx-auto text-center">
          <div className="mb-4 space-x-6">
            <Link
              to="/privacy"
              className="text-gray-400 hover:text-white transition-colors"
            >
              Privacy Policy
            </Link>
            <Link
              to="/terms-of-service"
              className="text-gray-400 hover:text-white transition-colors"
            >
              Terms of Service
            </Link>
            <Link
              to="/google-api-disclosure"
              className="text-gray-400 hover:text-white transition-colors"
            >
              Google API Disclosure
            </Link>
            <Link
              to="/microsoft-api-disclosure"
              className="text-gray-400 hover:text-white transition-colors"
            >
              Microsoft API Disclosure
            </Link>
          </div>
          <p className="text-gray-400">© 2026 Jindo Technologies, LLC</p>
        </div>
      </footer>
    </div>
  );
};

export default GoogleAPIDisclosure;
